import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { CATEGORIAS } from "../lib/api";
import CategoriaBadge from "../components/CategoriaBadge";
import { useAuth } from "@/context/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";

import { CONCEPTO_LABEL, TIPOS } from "@/constants/recibos";

const PAGE_SIZE = 50;

const empty = {
  nombre: "",
  apellidos: "",
  fecha_nacimiento: "",
  dni: "",
  telefono: "",
  email: "",
  direccion: "",
  sexo: "",
  vende_loteria: false,
  foto_url: "",
  observaciones: "",
  activo: true,
  mandato_id: "",
  tarifa_overrides: [],
};

const emptyNuevoMandato = {
  referencia: "",
  iban: "",
  titular_nombre: "",
  tipo: "domiciliado",
  frecuencia: "anual",
  dia_pago: 1,
  fecha_firma: "",
  estado: "activo",
  notas: "",
};

const FRECUENCIAS_CENSO = [
  { value: "mensual", label: "Mensual" },
  { value: "bimensual", label: "Bimensual" },
  { value: "bimensual_impar", label: "Bimensual impar" },
  { value: "anual", label: "Anual" },
];

export default function Censo() {
  const [list, setList] = useState([]);
  const [mandatos, setMandatos] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.MANAGE_MEMBERS);
  const canManageRecibos = can(PERMISSIONS.MANAGE_RECIBOS);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [r, m] = await Promise.all([
        api.get("/falleros", {
          params: {
            search: debouncedSearch || undefined,
            categoria: filterCat || undefined,
            include_inactive: showInactive,
          },
        }),
        api.get("/mandatos"),
      ]);
      setList(r.data);
      setMandatos(m.data);
    } catch (err) {
      toast.error("Error cargando censo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [debouncedSearch, filterCat, showInactive]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return list.slice(start, start + PAGE_SIZE);
  }, [list, page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...modal.data,
        sexo: modal.data.sexo || null,
        vende_loteria: !!modal.data.vende_loteria,
        mandato_id:
          modal.mandatoMode === "existente"
            ? modal.data.mandato_id || null
            : null,
      };
      if (modal.mandatoMode === "nuevo" && !modal.isEdit) {
        payload.nuevo_mandato = {
          ...modal.nuevoMandato,
          dia_pago: Number(modal.nuevoMandato.dia_pago) || 1,
          fecha_firma: modal.nuevoMandato.fecha_firma || null,
        };
      }
      if (modal.isEdit) {
        delete payload.nuevo_mandato;
        await api.put(`/falleros/${modal.data.id}`, payload);
        toast.success("Fallero actualizado");
      } else {
        await api.post("/falleros", payload);
        toast.success(
          modal.mandatoMode === "nuevo"
            ? "Fallero y mandato creados"
            : "Fallero añadido",
        );
      }
      setModal(null);
      fetchData();
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };

  const onDelete = async (f) => {
    if (!window.confirm(`¿Eliminar a ${f.nombre} ${f.apellidos}?`)) return;
    await api.delete(`/falleros/${f.id}`);
    toast.success("Eliminado");
    fetchData();
  };

  const addOverride = () => {
    setModal({
      ...modal,
      data: {
        ...modal.data,
        tarifa_overrides: [
          ...(modal.data.tarifa_overrides || []),

          {
            tipo: "cuota",
            importe: 0,
            enabled: true,
          },
        ],
      },
    });
  };

  const removeOverride = (idx) => {
    setModal({
      ...modal,
      data: {
        ...modal.data,
        tarifa_overrides: modal.data.tarifa_overrides.filter(
          (_, i) => i !== idx,
        ),
      },
    });
  };

  const updateOverride = (idx, key, value) => {
    const next = [...(modal.data.tarifa_overrides || [])];

    next[idx] = {
      ...next[idx],
      [key]: value,
    };

    setModal({
      ...modal,
      data: {
        ...modal.data,
        tarifa_overrides: next,
      },
    });
  };

  return (
    <div className="p-6 md:p-10" data-testid="censo-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Falleros
          </div>
          <h1 className="font-display text-4xl font-bold text-med-text">
            Censo
          </h1>
          <p className="text-med-muted mt-1">
            {list.length} fallero{list.length === 1 ? "" : "s"} registrado
            {list.length === 1 ? "" : "s"}.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() =>
              setModal({
                data: { ...empty },
                isEdit: false,
                mandatoMode: "ninguno",
                nuevoMandato: { ...emptyNuevoMandato },
              })
            }
            data-testid="add-fallero-btn"
            className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold transition-colors active:scale-[0.98]"
          >
            <Plus size={18} weight="bold" />
            Nuevo fallero
          </button>
        )}
      </header>

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={18}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-med-muted"
          />
          <input
            type="text"
            placeholder="Buscar por nombre, DNI o email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-med-surface border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 outline-none transition-all"
          />
        </div>
        <select
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          data-testid="filter-category"
          className="px-4 py-2.5 rounded-xl bg-med-surface border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 outline-none"
        >
          <option value="">Todas las categorías</option>
          {CATEGORIAS.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          id="show-inactive"
          className="w-4 h-4 accent-med-primary"
        />

        <label
          htmlFor="show-inactive"
          className="text-sm text-med-muted cursor-pointer"
        >
          Mostrar falleros inactivos
        </label>
      </div>

      <div className="bg-med-surface border border-med-border rounded-2xl overflow-hidden">
        <table className="w-full text-left" data-testid="falleros-table">
          <thead>
            <tr className="bg-med-surface2 text-xs uppercase tracking-[0.1em] text-med-muted font-semibold">
              <th className="px-5 py-3.5">Nombre</th>
              <th className="px-5 py-3.5">Categoría</th>
              <th className="px-5 py-3.5">Contacto</th>
              <th className="px-5 py-3.5">Sexo</th>
              <th className="px-5 py-3.5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-16 text-center text-med-muted"
                >
                  Cargando...
                </td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-5 py-16 text-center text-med-muted"
                >
                  No hay falleros que coincidan. Añade el primero arriba.
                </td>
              </tr>
            ) : (
              paginated.map((f) => (
                <tr
                  key={f.id}
                  className="border-t border-med-border/60 hover:bg-med-surface2/60 transition-colors"
                  data-testid={`fallero-row-${f.id}`}
                >
                  <td className="px-5 py-4">
                    <div className="font-semibold text-med-text">
                      {f.nombre} {f.apellidos}
                    </div>
                    {f.mandato_referencia && (
                      <div className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold uppercase tracking-wide text-med-secondary bg-med-secondary/10 px-1.5 py-0.5 rounded">
                        {f.mandato_referencia}
                      </div>
                    )}
                    <div className="mt-2">
                      {f.activo ? (
                        <span></span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          Inactivo
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <CategoriaBadge categoria={f.categoria} />
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="text-med-text">{f.email || "—"}</div>
                    <div className="text-xs text-med-muted">
                      {f.telefono || "—"}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm capitalize text-med-text">
                    {f.sexo || "—"}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-2">
                      {canManage ? (
                        <>
                          <button
                            onClick={() =>
                              setModal({
                                data: { ...f },
                                isEdit: true,
                                mandatoMode: f.mandato_id
                                  ? "existente"
                                  : "ninguno",
                                nuevoMandato: { ...emptyNuevoMandato },
                              })
                            }
                            data-testid={`edit-${f.id}`}
                            className="p-2 rounded-lg hover:bg-med-surface2 text-med-secondary transition-colors"
                          >
                            <PencilSimple size={18} weight="duotone" />
                          </button>
                          <button
                            onClick={() => onDelete(f)}
                            data-testid={`delete-${f.id}`}
                            className="p-2 rounded-lg hover:bg-med-danger/10 text-med-danger transition-colors"
                          >
                            <Trash size={18} weight="duotone" />
                          </button>
                        </>
                      ) : (
                        <span
                          data-testid={`readonly-fallero-${f.id}`}
                          className="text-[10px] uppercase tracking-wide text-med-muted italic"
                        >
                          Solo lectura
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Anterior
          </button>

          <div className="text-sm text-med-muted">
            Página {page} de {totalPages}
          </div>

          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          data-testid="fallero-modal"
          onClick={() => setModal(null)}
        >
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-med-surface border-b border-med-border px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-med-text">
                {modal.isEdit ? "Editar fallero" : "Nuevo fallero"}
              </h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { k: "nombre", l: "Nombre", req: true },
                { k: "apellidos", l: "Apellidos", req: true },
                {
                  k: "fecha_nacimiento",
                  l: "Fecha de nacimiento",
                  type: "date",
                  req: true,
                },
                { k: "dni", l: "DNI" },
                { k: "telefono", l: "Teléfono" },
                { k: "email", l: "Email", type: "email" },
                { k: "direccion", l: "Dirección", full: true },
                {
                  k: "sexo",
                  l: "Sexo",
                  select: true,
                  options: [
                    { value: "", label: "— Sin especificar —" },
                    { value: "hombre", label: "Hombre" },
                    { value: "mujer", label: "Mujer" },
                  ],
                },
                { k: "foto_url", l: "URL foto", full: true },
                {
                  k: "observaciones",
                  l: "Observaciones",
                  full: true,
                  textarea: true,
                },
              ].map((f) => (
                <div key={f.k} className={f.full ? "md:col-span-2" : ""}>
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    {f.l}
                    {f.req && <span className="text-med-primary"> *</span>}
                  </label>
                  {f.textarea ? (
                    <textarea
                      value={modal.data[f.k] || ""}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: {
                            ...modal.data,
                            [f.k]: e.target.value,
                          },
                        })
                      }
                      rows={3}
                      data-testid={`field-${f.k}`}
                      className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-all"
                    />
                  ) : f.select ? (
                    <select
                      value={modal.data[f.k] || ""}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: {
                            ...modal.data,
                            [f.k]: e.target.value,
                          },
                        })
                      }
                      data-testid={`field-${f.k}`}
                      className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-all"
                    >
                      {f.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={f.type || "text"}
                      required={f.req}
                      step={f.type === "number" ? "0.01" : undefined}
                      value={modal.data[f.k] ?? ""}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: {
                            ...modal.data,
                            [f.k]: e.target.value,
                          },
                        })
                      }
                      data-testid={`field-${f.k}`}
                      className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none transition-all"
                    />
                  )}
                </div>
              ))}

              {/* Vende lotería */}
              <div className="md:col-span-2 flex items-center gap-3 bg-med-surface2/60 rounded-xl px-4 py-3">
                <input
                  type="checkbox"
                  checked={!!modal.data.vende_loteria}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, vende_loteria: e.target.checked },
                    })
                  }
                  data-testid="field-vende_loteria"
                  className="w-4 h-4 accent-med-primary"
                  id="vende-loteria-check"
                />
                <label
                  htmlFor="vende-loteria-check"
                  className="text-sm text-med-text cursor-pointer flex-1"
                >
                  <span className="font-semibold">Vende lotería</span>
                  <span className="block text-xs text-med-muted">
                    Si está activo, en los recibos NO se cobrará el "Beneficio
                    de lotería" (lo gana con la venta).
                  </span>
                </label>
              </div>

              {/* Activo */}
              <div className="md:col-span-2 flex items-center gap-3 bg-med-surface2/60 rounded-xl px-4 py-3">
                <input
                  type="checkbox"
                  checked={!!modal.data.activo}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: {
                        ...modal.data,
                        activo: e.target.checked,
                      },
                    })
                  }
                  data-testid="field-activo"
                  className="w-4 h-4 accent-med-primary"
                  id="activo-check"
                />

                <label
                  htmlFor="activo-check"
                  className="text-sm text-med-text cursor-pointer flex-1"
                >
                  <span className="font-semibold">Fallero activo</span>

                  <span className="block text-xs text-med-muted">
                    Si está inactivo no podrá acceder al sistema ni participar
                    en eventos.
                  </span>
                </label>
              </div>

              {/* Overrides de tarifas */}
              <div className="md:col-span-2 border-t border-med-border pt-5 mt-2">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-display text-lg font-bold text-med-text">
                      Tarifas personalizadas
                    </h4>

                    <p className="text-xs text-med-muted mt-1">
                      Sobrescribe importes o desactiva conceptos para este
                      fallero.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={addOverride}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-med-secondary text-white text-sm font-semibold"
                  >
                    <Plus size={16} weight="bold" />
                    Añadir override
                  </button>
                </div>

                <div className="space-y-3">
                  {(modal.data.tarifa_overrides || []).map((o, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-12 gap-3 items-center bg-med-surface2 rounded-xl p-3"
                    >
                      <select
                        value={o.tipo}
                        onChange={(e) =>
                          updateOverride(idx, "tipo", e.target.value)
                        }
                        className="col-span-4 rounded-xl bg-white border border-med-border px-3 py-2"
                      >
                        {TIPOS.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {CONCEPTO_LABEL[tipo]}
                          </option>
                        ))}
                      </select>

                      <input
                        type="number"
                        step="0.01"
                        value={o.importe ?? ""}
                        onChange={(e) =>
                          updateOverride(
                            idx,
                            "importe",
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                          )
                        }
                        className="col-span-3 rounded-xl bg-white border border-med-border px-3 py-2"
                        placeholder="Importe"
                      />

                      <label className="col-span-3 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={o.enabled !== false}
                          onChange={(e) =>
                            updateOverride(idx, "enabled", e.target.checked)
                          }
                        />
                        Activo
                      </label>

                      <button
                        type="button"
                        onClick={() => removeOverride(idx)}
                        className="col-span-2 inline-flex justify-center p-2 rounded-lg text-med-danger hover:bg-med-danger/10"
                      >
                        <Trash size={18} weight="duotone" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mandato bancario section */}
              <div className="md:col-span-2 border-t border-med-border pt-5 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display text-lg font-bold text-med-text">
                    Mandato bancario
                  </h4>
                  {modal.isEdit && (
                    <span className="text-[10px] uppercase tracking-wide text-med-muted">
                      Para crear un mandato nuevo, hazlo desde la pestaña
                      Mandatos
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { v: "ninguno", l: "Sin mandato" },
                    { v: "existente", l: "Asignar existente" },
                    ...(modal.isEdit || !canManageRecibos
                      ? []
                      : [{ v: "nuevo", l: "+ Crear nuevo mandato" }]),
                  ].map((opt) => (
                    <button
                      type="button"
                      key={opt.v}
                      onClick={() => setModal({ ...modal, mandatoMode: opt.v })}
                      data-testid={`mandato-mode-${opt.v}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                        modal.mandatoMode === opt.v
                          ? "bg-med-primary text-white"
                          : "bg-med-surface2 text-med-text hover:bg-med-border"
                      }`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
                {!canManageRecibos && !modal.isEdit && (
                  <p
                    className="text-[11px] text-med-muted italic mb-3"
                    data-testid="censo-mandato-perm-hint"
                  >
                    Crear un mandato nuevo requiere también el permiso{" "}
                    <span className="font-mono">manage_recibos</span>. Pide al
                    tesorero que lo haga después.
                  </p>
                )}

                {modal.mandatoMode === "existente" && (
                  <select
                    value={modal.data.mandato_id || ""}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: { ...modal.data, mandato_id: e.target.value },
                      })
                    }
                    data-testid="field-mandato_id"
                    className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  >
                    <option value="">— Selecciona mandato —</option>
                    {mandatos.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.referencia} · {m.iban} (
                        {m.tipo === "domiciliado"
                          ? "Domiciliado"
                          : "Pago en mano"}
                        )
                      </option>
                    ))}
                  </select>
                )}

                {modal.mandatoMode === "nuevo" && !modal.isEdit && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-med-surface2/60 rounded-xl p-4 border border-med-border">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                        Referencia <span className="text-med-primary">*</span>
                      </label>
                      <input
                        required
                        value={modal.nuevoMandato.referencia}
                        onChange={(e) =>
                          setModal({
                            ...modal,
                            nuevoMandato: {
                              ...modal.nuevoMandato,
                              referencia: e.target.value,
                            },
                          })
                        }
                        data-testid="nm-referencia"
                        placeholder="MAND-2026-0002"
                        className="w-full font-mono rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                      />
                    </div>
                    {modal.nuevoMandato.tipo === "domiciliado" && (
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                          IBAN <span className="text-med-primary">*</span>
                        </label>
                        <input
                          required
                          value={modal.nuevoMandato.iban}
                          onChange={(e) =>
                            setModal({
                              ...modal,
                              nuevoMandato: {
                                ...modal.nuevoMandato,
                                iban: e.target.value.toUpperCase(),
                              },
                            })
                          }
                          data-testid="nm-iban"
                          placeholder="ES76 …"
                          className="w-full font-mono rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                        />
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                        Titular (opcional)
                      </label>
                      <input
                        value={modal.nuevoMandato.titular_nombre}
                        onChange={(e) =>
                          setModal({
                            ...modal,
                            nuevoMandato: {
                              ...modal.nuevoMandato,
                              titular_nombre: e.target.value,
                            },
                          })
                        }
                        data-testid="nm-titular"
                        placeholder={`Si vacío, se usará: ${modal.data.nombre || "(nombre)"} ${modal.data.apellidos || "(apellidos)"}`}
                        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                        Tipo
                      </label>
                      <select
                        value={modal.nuevoMandato.tipo}
                        onChange={(e) =>
                          setModal({
                            ...modal,
                            nuevoMandato: {
                              ...modal.nuevoMandato,
                              tipo: e.target.value,
                            },
                          })
                        }
                        data-testid="nm-tipo"
                        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                      >
                        <option value="domiciliado">Domiciliado</option>
                        <option value="pago_en_mano">Pago en mano</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                        Fecha de firma
                      </label>
                      <input
                        type="date"
                        value={modal.nuevoMandato.fecha_firma || ""}
                        onChange={(e) =>
                          setModal({
                            ...modal,
                            nuevoMandato: {
                              ...modal.nuevoMandato,
                              fecha_firma: e.target.value,
                            },
                          })
                        }
                        className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                      />
                    </div>
                    {modal.nuevoMandato.tipo === "domiciliado" && (
                      <>
                        <div>
                          <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                            Frecuencia
                          </label>
                          <select
                            value={modal.nuevoMandato.frecuencia}
                            onChange={(e) =>
                              setModal({
                                ...modal,
                                nuevoMandato: {
                                  ...modal.nuevoMandato,
                                  frecuencia: e.target.value,
                                },
                              })
                            }
                            className="w-full rounded-xl bg-white border border-med-border focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                          >
                            {FRECUENCIAS_CENSO.map((f) => (
                              <option key={f.value} value={f.value}>
                                {f.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                            Día de pago
                          </label>
                          <div className="grid grid-cols-3 gap-2">
                            {[1, 5, 10].map((d) => (
                              <button
                                type="button"
                                key={d}
                                onClick={() =>
                                  setModal({
                                    ...modal,
                                    nuevoMandato: {
                                      ...modal.nuevoMandato,
                                      dia_pago: d,
                                    },
                                  })
                                }
                                data-testid={`nm-dia-${d}`}
                                className={`py-2 rounded-lg border-2 text-sm font-semibold transition-colors ${
                                  Number(modal.nuevoMandato.dia_pago) === d
                                    ? "border-med-primary bg-med-primary/10 text-med-primary"
                                    : "border-med-border text-med-text hover:border-med-secondary"
                                }`}
                              >
                                Día {d}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-med-surface border-t border-med-border px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="save-fallero-btn"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold transition-colors active:scale-[0.98]"
              >
                {modal.isEdit ? "Guardar cambios" : "Crear fallero"}
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
}
